import { isPast, localeAwareFormat } from 'app/lib/date';
import type { Loan } from 'app/types/loan';
import { LoanStatus, LoanType } from 'app/types/loan';
import React from 'react';
import { differenceInCalendarDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getLoanInfos } from '../functions';
import type { User } from 'app/types/user';
import { Text } from 'app/design/primitives/text';
import { Column, Row } from 'app/design/primitives/box';
import { Platform, View } from 'react-native';
import { HistoryLine } from 'app/design/icons/history-line';
import { HistoryCircle } from 'app/design/icons/history-circle';

import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { createTheme as createDSTheme } from 'app/shopify';
import type { TFunction } from 'i18next';
const theme = createDSTheme();

interface HistorySectionProps {
  loan?: Loan | null;
  user?: User;
}

interface HistoryItem {
  color: string;
  text: string;
}

function getReturnTiming(loan?: Loan | null) {
  if (!loan) {
    return { isSoftReturned: false, numDaysLate: 0 };
  }

  // Before we started tracking returned dates, let's assume a Completed loan was returned on time
  const isSoftReturned = loan.status === LoanStatus.Completed && !loan.returned;
  const returnedAt = isSoftReturned ? loan.end : loan.returned;

  return {
    isSoftReturned,
    numDaysLate: differenceInCalendarDays(returnedAt ? new Date(returnedAt) : new Date(), new Date(loan.end)),
  };
}

function getReturnedBy({
  loan,
  loanType,
  otherUser,
  t,
  tPrefix,
}: {
  loan: Loan;
  loanType?: LoanType;
  otherUser?: Loan['borrower'];
  t: TFunction<'one-loan'>;
  tPrefix: string;
}) {
  if (loan.status === LoanStatus.Returned) {
    return loanType === LoanType.Lending ? otherUser?.profile?.firstName : t(`${tPrefix}.returned_by_you`);
  }

  return loanType === LoanType.Lending ? t(`${tPrefix}.returned_by_you`) : otherUser?.profile?.firstName;
}

function buildHistoryItems({
  isDonation,
  isSell,
  loan,
  loanType,
  otherUser,
  t,
  tPrefix,
}: {
  isDonation: boolean;
  isSell: boolean;
  loan?: Loan | null;
  loanType?: LoanType;
  otherUser?: Loan['borrower'];
  t: TFunction<'one-loan'>;
  tPrefix: string;
}): HistoryItem[] {
  const localizeDate = (date: string) => localeAwareFormat(new Date(date), t('dateFormat_MMMMdYYYY', { ns: 'common' }));
  const { isSoftReturned, numDaysLate } = getReturnTiming(loan);
  const historyItems: HistoryItem[] = [];
  let iconColor: string = theme.colors['text.tertiary'];

  if (loan?.status === LoanStatus.Pending) {
    historyItems.push({
      color: iconColor,
      text: t(`${tPrefix}.offered_when`, { date: localizeDate(loan.createdAt) }),
    });
    return historyItems;
  }

  if (loan?.accepted) {
    historyItems.push({
      color: iconColor,
      text: t(`${tPrefix}.accepted_when`, { date: localizeDate(loan.accepted) }),
    });
  }

  if (loan?.start && isPast(new Date(loan.start))) {
    historyItems.push({
      color: iconColor,
      text: t(`${tPrefix}.started_when`, { date: localizeDate(loan.start) }),
    });
  }

  if (loan?.guardianAssignedAt) {
    historyItems.push({
      color: theme.colors['text.primary'],
      text: t('history', {
        ns: 'guardian',
        name: loan.borrower.profile?.firstName ?? '',
        date: localizeDate(loan.guardianAssignedAt),
      }),
    });
    return historyItems;
  }

  if (loan?.end && isPast(new Date(loan.end)) && !isDonation && !isSell) {
    historyItems.push({
      color: iconColor,
      text: t(`${tPrefix}.end_when`, { date: localizeDate(loan.end) }),
    });
  }

  if (numDaysLate > 0) {
    iconColor = theme.colors['surface.error.default'];
    historyItems.push({
      color: iconColor,
      text: t(numDaysLate === 1 ? `${tPrefix}.late_yesterday` : `${tPrefix}.late_x_days`, {
        numDays: numDaysLate,
      }),
    });
  }

  if (loan?.returned && !isSoftReturned) {
    historyItems.push({
      color: iconColor,
      text: t(`${tPrefix}.returned_when`, {
        who: getReturnedBy({ loan, loanType, otherUser, t, tPrefix }),
        date: localizeDate(loan.returned),
      }),
    });
  }

  return historyItems;
}

export function History({ loan, user }: HistorySectionProps) {
  const { t } = useTranslation('one-loan');

  const isService = loan?.listing.subtype === ListingSubtype.service;
  const isDonation = isDonationListing(loan?.listing);
  const isSell = loan?.listing.transactionMode === TransactionMode.sell;
  const tPrefix = isSell ? 'saleHistory' : isService ? 'serviceHistory' : isDonation ? 'donationHistory' : 'history';

  const { loanType, otherUser } = (() => {
    return getLoanInfos(loan, user);
  })();

  const historyItems = buildHistoryItems({ isDonation, isSell, loan, loanType, otherUser, t, tPrefix });

  return (
    <Column gap="spacing.general.x4">
      {historyItems.length > 0 && (
        <Text fontSize={16} fontWeight="bold" variant="bodyBase">
          {t(`${tPrefix}.title`)}
        </Text>
      )}

      <Row style={{ flexDirection: 'row', flex: 1, marginLeft: 2 }}>
        <Column flex={0} style={{ paddingTop: 5 }}>
          {historyItems.map((historyItem, index) => (
            <View key={historyItem.text}>
              {index > 0 && (
                <HistoryLine color={historyItem.color} style={{ marginTop: 2, marginBottom: 3, marginLeft: 2 }} />
              )}
              <HistoryCircle color={historyItem.color} />
            </View>
          ))}
        </Column>
        <Column flex={1} marginLeft="spacing.general.x4" style={{ marginTop: Platform.OS === 'android' ? -3 : 0 }}>
          {historyItems.map((historyItem) => (
            <Text
              key={historyItem.text}
              numberOfLines={1}
              style={{ marginBottom: Platform.OS === 'android' ? 15.5 : 18 }}
            >
              {historyItem.text}
            </Text>
          ))}
        </Column>
      </Row>
    </Column>
  );
}
